//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import ContractActions from 'common/redux/actions/contract';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import ContractSelectors from 'common/redux/selectors/contract';
import { PaginatedTable } from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';
import VisitUtils from 'common/utils/VisitUtils';
import { CellDataTypes } from 'common/components/core/table/CellDataTypes';
import EffectiveStatusDropdown from './EffectiveStatusDropdown';

import theme from './ContractVersionsTable.scss';
import { SizeEnum } from 'common/enums/UIEnums';
import {
    PermissionMatch,
    RoleOperationEnum,
    AccessDeniedMessage
} from 'common/modules/rbac';
import { Tooltip } from '@locus-taxy/neo-ui';

const COLUMNS = [
    {
        headerTextId: 'contractTable.version',
        sortable: false,
        filterable: false,
        minWidth: 100,
        Cell: props => {
            const {
                original: { version }
            } = props;
            return <span>{`version ${version}`}</span>;
        }
    },
    {
        headerTextId: 'contractTable.effectiveStatus',
        accessor: 'effectiveStatus',
        minWidth: 100,
        infixSearchable: true,
        Cell: props => {
            const {
                original: {
                    effectiveStatus,
                    nextEffectiveStatuses,
                    clientId,
                    id
                },
                tdProps: {
                    rest: { fetchContractVersions, t }
                },
                index
            } = props;
            return (
                <PermissionMatch
                    operations={RoleOperationEnum.UPDATE_CONTRACT}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={hasPermission => (
                        <Tooltip
                            shouldWrapChildren
                            tooltip={
                                hasPermission ? null : (
                                    <AccessDeniedMessage action={t('update')} />
                                )
                            }
                        >
                            <EffectiveStatusDropdown
                                value={effectiveStatus}
                                options={nextEffectiveStatuses}
                                clientId={clientId}
                                contractId={id}
                                fetchContractVersions={fetchContractVersions}
                                disabled={!hasPermission || index !== 0}
                            />
                        </Tooltip>
                    )}
                />
            );
        }
    },
    {
        headerTextId: 'contractTable.description',
        accessor: 'description',
        minWidth: 300,
        infixSearchable: true
    },
    {
        headerTextId: 'contractTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: contract,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                contract?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'contractTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'contractTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: contract,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                contract?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'contractTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

class ContractVersionsTable extends PureComponent {
    columns: Array<Object>;
    constructor(props) {
        super(props);
        this.columns = COLUMNS.map((rawCol: Object) => {
            const { headerTextId } = rawCol;
            let column = rawCol;
            if (headerTextId) {
                column = {
                    ...rawCol,
                    headerText: props.t(headerTextId)
                };
            }
            return column;
        });
    }
    fetchVersions = contractId => {
        const { fetchContractVersions, clientId } = this.props;
        fetchContractVersions({ contractId, clientId });
    };

    onSubTableStateChange = () => {
        const { row } = this.props;
        const { original: contract } = row;
        this.fetchVersions(contract.id);
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { fetchContractVersions, t } = this.props;
        return {
            fetchContractVersions,
            t,
            theme
        };
    };

    render() {
        const {
            contractVersionData,
            isVersionTableLoading,
            activeVersion,
            t
        } = this.props;
        return (
            <PaginatedTable
                theme={theme}
                loading={isVersionTableLoading}
                activeVersion={activeVersion}
                data={
                    contractVersionData
                        ? contractVersionData.entities.reverse().toJS()
                        : []
                }
                totalPages={null}
                noDataText={!isVersionTableLoading ? t('noRowsFound') : ''}
                columns={this.columns}
                defaultPageSize={null}
                onTableStateChange={this.onSubTableStateChange}
                getTdProps={this.getTdProps}
                stickyHeader={false}
                loaderSize={SizeEnum.Small}
                useLocalisation
            />
        );
    }
}

const contractStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isVersionTableLoading = ContractSelectors.getIsEntityVersionsLoading(
        state,
        ownProps
    );
    const contractVersionData = ContractSelectors.getEntityVersions(
        state,
        ownProps
    );

    return {
        clientId,
        contractVersionData,
        isVersionTableLoading
    };
};

const ThemedContractVersionsTable = themr(null, theme)(ContractVersionsTable);

export default withRouter(
    connect(contractStateToProps, {
        fetchContractVersions: ContractActions.fetchEntityVersions
    })(ThemedContractVersionsTable)
);
